import { Ionicons } from '@expo/vector-icons';
import React, { useState } from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';

import { Button, Modal, Text } from '@/components/ui';

/** The basis-aware reports that can supply a report-specific details section. */
export type BasisReport = 'trial-balance' | 'balance-sheet' | 'profit-loss' | 'gl-report';

/**
 * Small (ⓘ) button that opens a modal explaining cash vs accrual basis.
 * Renders inline next to a "Basis" label on basis-aware reports. Pass `report`
 * to add a "What changes on this report" section spelling out exactly which
 * rows move when the basis flips on that specific report.
 */
export const BasisHelpButton: React.FC<{ report?: BasisReport }> = ({ report }) => {
	const [open, setOpen] = useState(false);
	return (
		<>
			<Pressable
				onPress={() => setOpen(true)}
				accessibilityRole="button"
				accessibilityLabel="What's the difference between cash and accrual basis?"
				hitSlop={8}
				style={({ pressed }) => [styles.iconBtn, pressed && styles.iconBtnPressed]}
			>
				<Ionicons name="information-circle-outline" size={16} color="#1976d2" />
			</Pressable>
			<Modal
				visible={open}
				onDismiss={() => setOpen(false)}
				contentContainerStyle={styles.modal}
			>
				<BasisHelpContent onClose={() => setOpen(false)} report={report} />
			</Modal>
		</>
	);
};

interface ReportDetail {
	name: string;
	points: Array<{ label: string; body: string }>;
	unchanged: string;
}

// What EXACTLY moves on each basis-aware report when the basis flips. Shown as
// a dedicated "details" section so the explanation is concrete to the report
// the user is actually looking at, not just the generic cash-vs-accrual idea.
const REPORT_DETAILS: Record<BasisReport, ReportDetail> = {
	'trial-balance': {
		name: 'Trial Balance',
		points: [
			{
				label: 'AR and AP rows are dropped',
				body: "They don't exist on a cash basis.",
			},
			{
				label: 'Income and Expense amounts shrink',
				body:
					"They're re-recognized at the payment date and any unpaid invoices/bills are "
					+ 'omitted entirely. So a $1,200 invoice with only $1,000 received shows $1,200 of '
					+ 'income on accrual but $1,000 on cash; an unpaid vendor bill shows as expense on '
					+ 'accrual but $0 on cash.',
			},
			{
				label: 'Equity absorbs the difference',
				body:
					'Retained Earnings / Fund Balance shifts so total debits still equal total '
					+ 'credits. That’s the identity Cash Equity = Accrual Equity − AR + AP. In '
					+ 'this app that lands on the named equity GL (or the synthesized "Cash Basis '
					+ 'Adjustment 3998" plug if there’s a residual).',
			},
		],
		unchanged:
			'Bank and every other real asset/liability (prepaid, fixed assets, long-term '
			+ 'liabilities) — cash moved when it moved, so those rows are identical in both bases.',
	},
	'balance-sheet': {
		name: 'Balance Sheet',
		points: [
			{
				label: 'AR and AP are removed',
				body:
					"Accounts Receivable and Accounts Payable don't appear on a cash-basis Balance "
					+ 'Sheet — they are the bridge accounts between billed and paid.',
			},
			{
				label: 'Equity absorbs the difference',
				body:
					'Retained Earnings / Fund Balance shifts by exactly −AR + AP, so Assets = '
					+ 'Liabilities + Equity still holds. That’s the identity Cash Equity = Accrual '
					+ 'Equity − AR + AP.',
			},
		],
		unchanged:
			'Bank and every other real asset/liability (prepaid, fixed assets, long-term '
			+ 'liabilities) are identical in both bases. There are no income/expense rows on the '
			+ 'Balance Sheet — that timing difference reaches it only through equity.',
	},
	'profit-loss': {
		name: 'Profit & Loss',
		points: [
			{
				label: 'Income and Expense are re-timed',
				body:
					'Each line is recognized on the payment date instead of the invoice/bill date. '
					+ 'Unpaid invoices and bills are omitted entirely; partial payments count '
					+ 'proportionally on the date paid. So a $1,200 invoice with $1,000 received shows '
					+ '$1,200 of income on accrual but $1,000 on cash; an unpaid vendor bill shows as '
					+ 'expense on accrual but $0 on cash.',
			},
			{
				label: 'Net Income shifts',
				body: 'Cash Net Income = Accrual Net Income − ΔAR + ΔAP over the period.',
			},
		],
		unchanged:
			"AR and AP never appear on a P&L (they're Balance Sheet accounts), and direct cash "
			+ 'sales / cash expenses with no AR/AP leg count the same in both bases.',
	},
	'gl-report': {
		name: 'GL Report',
		points: [
			{
				label: 'AR and AP lines are suppressed',
				body:
					'Journal lines hitting the Accounts Receivable / Accounts Payable control accounts '
					+ 'drop out on cash basis.',
			},
			{
				label: 'Income and Expense lines are re-timed',
				body:
					'They surface on the payment date instead of the invoice/bill date; lines whose '
					+ "AR/AP leg hasn't settled by the report date are omitted.",
			},
		],
		unchanged:
			'Bank and every other real asset/liability line is unchanged — the cash movements are '
			+ 'basis-independent. This is the same filter the Trial Balance and Balance Sheet build on.',
	},
};

const ROWS: Array<{ q: string; accrual: string; cash: string }> = [
	{
		q: 'When do we count revenue (income)?',
		accrual: 'On the invoice date',
		cash: 'On the date payment is received (check, etc.)',
	},
	{
		q: 'When do we count an expense?',
		accrual: 'On the bill date',
		cash: 'On the date the payment / check is issued',
	},
	{
		q: 'Are AR / AP on the Balance Sheet?',
		accrual: 'Yes — real asset / liability',
		cash: "No — they don't exist on cash-basis BS",
	},
];

const BasisHelpContent: React.FC<{ onClose: () => void; report?: BasisReport }> = ({ onClose, report }) => {
	const detail = report ? REPORT_DETAILS[report] : null;
	return (
		<View>
			<View style={styles.header}>
				<Text style={styles.title}>Cash vs. Accrual basis</Text>
				<Pressable onPress={onClose} hitSlop={8} accessibilityRole="button" accessibilityLabel="Close">
					<Ionicons name="close" size={22} color="#455a64" />
				</Pressable>
			</View>

			<ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
				{detail && (
					<View style={styles.detailCard}>
						<Text style={styles.detailHeading}>What changes on the {detail.name} when you switch basis</Text>
						{detail.points.map((p, i) => (
							<Text key={p.label} style={[styles.detailPoint, i > 0 && styles.detailPointSpaced]}>
								<Text style={styles.detailNum}>{i + 1}. </Text>
								<Text style={styles.detailLabel}>{p.label}</Text>
								<Text> — {p.body}</Text>
							</Text>
						))}
						<Text style={styles.detailUnchangedHeading}>What does not change</Text>
						<Text style={styles.detailUnchanged}>{detail.unchanged}</Text>
					</View>
				)}

				<View style={styles.table}>
					<View style={[styles.row, styles.headerRow]}>
						<Text style={[styles.cell, styles.cellQ, styles.headerText]}>Question</Text>
						<Text style={[styles.cell, styles.cellA, styles.headerText]}>Accrual answer</Text>
						<Text style={[styles.cell, styles.cellA, styles.headerText]}>Cash answer</Text>
					</View>
					{ROWS.map((r, i) => (
						<View key={r.q} style={[styles.row, i === ROWS.length - 1 && styles.rowLast]}>
							<Text style={[styles.cell, styles.cellQ, styles.qText]}>{r.q}</Text>
							<Text style={[styles.cell, styles.cellA]}>{r.accrual}</Text>
							<Text style={[styles.cell, styles.cellA]}>{r.cash}</Text>
						</View>
					))}
				</View>

				<Text style={styles.summary}>
				Accrual recognizes a transaction when it is <Text style={styles.bold}>billed</Text> (the
				invoice or bill date); cash recognizes it when the{' '}
					<Text style={styles.bold}>payment is received or issued</Text>. AR and AP are the bridge
				accounts that hold the gap between billed and paid — cash basis suppresses them.
				</Text>

				<View style={styles.identityCard}>
					<Text style={styles.identityLabel}>The identity that keeps everything balanced</Text>
					<Text style={styles.identityLine}>
					Cash NI = Accrual NI − ΔAR + ΔAP <Text style={styles.identityDim}>(over period)</Text>
					</Text>
					<Text style={styles.identityLine}>
					Cash Equity = Accrual Equity − AR + AP <Text style={styles.identityDim}>(at as-of)</Text>
					</Text>
				</View>

				<Text style={styles.tipHeading}>Where the basis selector matters</Text>
				<Text style={styles.tipBody}>
				Balance Sheet, P&L, GL Report, Trial Balance. Aging reports, statements, the journal, and
				entity lists (bills/invoices/payments/etc.) are basis-independent — they show the same data
				either way.
				</Text>
			</ScrollView>

			<View style={styles.actions}>
				<Button mode="contained" onPress={onClose}>Close</Button>
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	iconBtn: { padding: 2, marginLeft: 4, opacity: 0.85 },
	iconBtnPressed: { opacity: 0.5 },

	modal: { width: 640, maxWidth: '95%' },

	header: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		marginBottom: 12,
	},
	title: { fontSize: 18, fontWeight: '700', color: '#0d47a1' },

	scroll: { maxHeight: 480 },
	scrollContent: { gap: 14, paddingBottom: 4 },

	detailCard: {
		backgroundColor: '#e3f2fd',
		borderWidth: 1,
		borderColor: '#90caf9',
		borderRadius: 8,
		padding: 12,
		gap: 2,
	},
	detailHeading: { fontSize: 13, fontWeight: '700', color: '#0d47a1', marginBottom: 6 },
	detailPoint: { fontSize: 13, color: '#1a2733', lineHeight: 19 },
	detailPointSpaced: { marginTop: 8 },
	detailNum: { fontWeight: '700', color: '#0d47a1' },
	detailLabel: { fontWeight: '700', color: '#0d47a1' },
	detailUnchangedHeading: {
		fontSize: 11, fontWeight: '700', color: '#33691e',
		textTransform: 'uppercase', letterSpacing: 0.4, marginTop: 12, marginBottom: 2,
	},
	detailUnchanged: { fontSize: 13, color: '#1b5e20', lineHeight: 19 },

	table: {
		borderWidth: 1,
		borderColor: '#cfd8dc',
		borderRadius: 8,
		overflow: 'hidden',
	},
	row: {
		flexDirection: 'row',
		borderBottomWidth: 1,
		borderBottomColor: '#e0e7ef',
		minHeight: 44,
	},
	rowLast: { borderBottomWidth: 0 },
	headerRow: { backgroundColor: '#eceff1' },
	cell: {
		paddingHorizontal: 10,
		paddingVertical: 10,
		fontSize: 13,
		color: '#263238',
		borderRightWidth: 1,
		borderRightColor: '#e0e7ef',
		minWidth: 0,
	},
	cellQ: { flexGrow: 2, flexBasis: 200, fontWeight: '600' },
	cellA: { flexGrow: 3, flexBasis: 180 },
	headerText: {
		fontSize: 11,
		fontWeight: '700',
		color: '#546e7a',
		textTransform: 'uppercase',
		letterSpacing: 0.4,
	},
	qText: { color: '#0d47a1' },

	summary: { fontSize: 13, color: '#37474f', lineHeight: 19 },
	bold: { fontWeight: '700', color: '#0d47a1' },

	identityCard: {
		backgroundColor: '#f1f8e9',
		borderWidth: 1,
		borderColor: '#c5e1a5',
		borderRadius: 8,
		padding: 12,
		gap: 4,
	},
	identityLabel: {
		fontSize: 11,
		fontWeight: '700',
		color: '#33691e',
		textTransform: 'uppercase',
		letterSpacing: 0.4,
		marginBottom: 4,
	},
	identityLine: { fontSize: 13, fontFamily: 'Menlo', color: '#1b5e20' },
	identityDim: { color: '#558b2f', fontStyle: 'italic' },

	tipHeading: {
		fontSize: 11,
		fontWeight: '700',
		color: '#546e7a',
		textTransform: 'uppercase',
		letterSpacing: 0.4,
		marginTop: 4,
	},
	tipBody: { fontSize: 13, color: '#37474f', lineHeight: 19 },

	actions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 8, marginTop: 16 },
});
